import { useEffect, useMemo, useState } from "react";
import type { ComponentType, ReactNode, SelectHTMLAttributes } from "react";
import { Car, ChevronDown, ChevronRight, Pencil, Sparkles, Trash2, UserPlus, Users, X } from "lucide-react";
import type { ClientDTO, ServiceTargetDTO } from "@tendvia/shared";
import { api } from "../api";
import { Button } from "../components/Button";
import { Field } from "../components/Field";

type ClientsPanelProps = {
  clients: ClientDTO[];
  targets: ServiceTargetDTO[];
  onRefresh: () => Promise<void>;
};

type ActiveSheet = "client" | "vehicle" | "remove-vehicle" | null;

export function ClientsPanel({ clients, targets, onRefresh }: ClientsPanelProps) {
  const [activeSheet, setActiveSheet] = useState<ActiveSheet>(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientNotes, setClientNotes] = useState("");

  const [vehicleClientId, setVehicleClientId] = useState("");
  const [vehicleLabel, setVehicleLabel] = useState("");
  const [vehicleAddress, setVehicleAddress] = useState("");
  const [vehicleYear, setVehicleYear] = useState("");
  const [vehicleMake, setVehicleMake] = useState("");
  const [vehicleModel, setVehicleModel] = useState("");
  const [editingVehicleId, setEditingVehicleId] = useState<string | null>(null);
  const [targetPendingRemoval, setTargetPendingRemoval] = useState<ServiceTargetDTO | null>(null);
  const [expandedClients, setExpandedClients] = useState<Record<string, boolean>>({});

  const targetsByClient = useMemo(() => {
    return targets.reduce<Record<string, ServiceTargetDTO[]>>((accumulator, target) => {
      if (target.status !== "active") return accumulator;
      (accumulator[target.clientId] ??= []).push(target);
      return accumulator;
    }, {});
  }, [targets]);

  const savedAddresses = useMemo(() => {
    const seen = new Set<string>();
    return (targetsByClient[vehicleClientId] ?? [])
      .map((target) => target.address?.trim() ?? "")
      .filter((address) => {
        if (!address) return false;
        const normalized = address.toLowerCase();
        if (seen.has(normalized)) return false;
        seen.add(normalized);
        return true;
      });
  }, [targetsByClient, vehicleClientId]);

  const totalVehicles = targets.length;
  const hasClients = clients.length > 0;

  function resetClientForm() {
    setClientName("");
    setClientEmail("");
    setClientPhone("");
    setClientNotes("");
  }

  function resetVehicleForm(clientId = "") {
    setEditingVehicleId(null);
    setVehicleClientId(clientId);
    setVehicleLabel("");
    setVehicleAddress("");
    setVehicleYear("");
    setVehicleMake("");
    setVehicleModel("");
  }

  function openClientSheet() {
    setMessage("");
    resetClientForm();
    setActiveSheet("client");
  }

  function populateVehicleForm(target: ServiceTargetDTO, clientId = target.clientId) {
    const details = (target.details as Record<string, unknown> | null) ?? null;
    setEditingVehicleId(target.id);
    setVehicleClientId(clientId);
    setVehicleLabel(target.label);
    setVehicleAddress(target.address ?? "");
    setVehicleYear(stringValue(details?.year));
    setVehicleMake(stringValue(details?.make));
    setVehicleModel(stringValue(details?.model));
  }

  function openVehicleSheet(clientId?: string, target?: ServiceTargetDTO) {
    if (!hasClients) {
      setMessage("Add a client first, then you can attach a vehicle.");
      return;
    }
    setMessage("");
    if (target) {
      populateVehicleForm(target, clientId ?? target.clientId);
    } else {
      resetVehicleForm(clientId ?? clients[0]?.id ?? "");
    }
    setActiveSheet("vehicle");
  }

  function openRemoveVehicleSheet(target: ServiceTargetDTO) {
    setMessage("");
    setTargetPendingRemoval(target);
    setActiveSheet("remove-vehicle");
  }

  function closeSheet() {
    setActiveSheet(null);
    setTargetPendingRemoval(null);
  }

  async function createClient() {
    if (!clientName.trim()) return;
    setBusy(true);
    setMessage("");
    try {
      await api.createClient({
        name: clientName.trim(),
        email: clientEmail.trim() || null,
        phone: clientPhone.trim() || null,
        notes: clientNotes.trim() || null
      });
      resetClientForm();
      await onRefresh();
      setMessage("Client added.");
      closeSheet();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to create client.");
    } finally {
      setBusy(false);
    }
  }

  async function createVehicle() {
    if (!vehicleClientId || !vehicleLabel.trim()) return;
    setBusy(true);
    setMessage("");
    try {
      const payload = {
        clientId: vehicleClientId,
        type: "auto_detailing",
        label: vehicleLabel.trim(),
        address: vehicleAddress.trim() || null,
        details: {
          year: vehicleYear.trim() || null,
          make: vehicleMake.trim() || null,
          model: vehicleModel.trim() || null
        },
        status: "active" as const
      };

      if (editingVehicleId) {
        await api.updateServiceTarget(editingVehicleId, payload);
      } else {
        await api.createServiceTarget(payload);
      }
      resetVehicleForm(vehicleClientId);
      await onRefresh();
      setMessage(editingVehicleId ? "Vehicle profile updated." : "Vehicle profile added.");
      closeSheet();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to save vehicle profile.");
    } finally {
      setBusy(false);
    }
  }

  async function removeVehicle() {
    if (!targetPendingRemoval) return;
    setBusy(true);
    setMessage("");
    try {
      await api.updateServiceTarget(targetPendingRemoval.id, { status: "archived" });
      await onRefresh();
      setMessage("Vehicle removed.");
      closeSheet();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to remove vehicle.");
    } finally {
      setBusy(false);
    }
  }

  function toggleClientVehicles(clientId: string) {
    setExpandedClients((current) => ({ ...current, [clientId]: !current[clientId] }));
  }

  useEffect(() => {
    if (!activeSheet) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeSheet();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [activeSheet]);

  return (
    <div className="grid gap-5">
      <SectionHeader
        icon={Users}
        title="Clients"
        description="Store the customer first, then keep each vehicle and service address reusable."
      />

      {message && activeSheet === null ? <p className="rounded-token border border-border/60 bg-surface-soft px-3 py-2 text-sm font-medium text-muted">{message}</p> : null}

      <section className="relative grid gap-4">
        {clients.length === 0 ? (
          <EmptyState onAddClient={openClientSheet} />
        ) : (
          <div className="grid gap-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-primary">Client records</p>
                <h2 className="mt-2 text-xl font-extrabold text-text">{clients.length} client{clients.length === 1 ? "" : "s"} in your book</h2>
                <p className="mt-1 text-sm leading-6 text-muted">Keep the person who pays, the vehicle you service, and the saved address ready for the next quote.</p>
              </div>
              <span className="rounded-pill border border-border/50 bg-surface px-3 py-1.5 text-xs font-extrabold text-primary">
                {totalVehicles} vehicle{totalVehicles === 1 ? "" : "s"}
              </span>
            </div>

            <div className="grid gap-3">
              {clients.map((client) => {
                const clientTargets = targetsByClient[client.id] ?? [];
                const vehiclesExpanded = expandedClients[client.id] ?? false;
                return (
                  <article key={client.id} className="rounded-[1.35rem] border border-border/45 bg-surface p-3.5 sm:p-4">
                    <div className="grid gap-3">
                      <div className="flex min-w-0 items-start gap-3">
                        <IconMedallion icon={Users} />
                        <div className="min-w-0">
                          <p className="text-base font-extrabold leading-tight">{client.name}</p>
                          <p className="mt-1 text-sm text-muted">
                            {client.email ?? client.phone ?? "No contact saved"}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <button
                          type="button"
                          className="inline-flex min-h-10 items-center gap-2 rounded-token border border-border/45 bg-surface-soft px-3 py-2 text-sm font-extrabold text-text transition hover:bg-surface"
                          onClick={() => toggleClientVehicles(client.id)}
                        >
                          {vehiclesExpanded ? <ChevronDown className="size-4 text-primary" aria-hidden="true" /> : <ChevronRight className="size-4 text-primary" aria-hidden="true" />}
                          {clientTargets.length} vehicle{clientTargets.length === 1 ? "" : "s"}
                        </button>
                        <Button
                          variant="ghost"
                          icon={<Car className="size-4" aria-hidden="true" />}
                          type="button"
                          onClick={() => openVehicleSheet(client.id)}
                          className="min-h-10 shrink-0 rounded-token border border-border/50 bg-surface-soft px-3 text-text hover:bg-surface"
                        >
                          Add vehicle
                        </Button>
                      </div>
                    </div>

                    {vehiclesExpanded ? (
                      <div className="mt-3 grid gap-2">
                        {clientTargets.length === 0 ? (
                          <div className="flex items-center gap-3 rounded-[1rem] border border-dashed border-border/70 bg-surface-soft px-3 py-3 text-sm font-medium text-muted">
                            <Car className="size-4 text-primary" aria-hidden="true" />
                            No vehicles yet
                          </div>
                        ) : clientTargets.map((target) => (
                          <VehicleRow
                            key={target.id}
                            target={target}
                            onEdit={() => openVehicleSheet(client.id, target)}
                            onRemove={() => openRemoveVehicleSheet(target)}
                          />
                        ))}
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {hasClients ? (
        <button
          className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] right-4 z-20 inline-flex min-h-14 items-center gap-3 rounded-token border border-transparent bg-text px-4 py-3 text-sm font-extrabold text-primary-contrast shadow-float transition hover:bg-text/92 sm:right-6"
          type="button"
          onClick={openClientSheet}
        >
          <span className="inline-grid size-8 place-items-center rounded-token bg-primary-contrast/12">
            <UserPlus className="size-4" aria-hidden="true" />
          </span>
          Add client
        </button>
      ) : null}

      {activeSheet === "client" ? (
        <BottomSheet title="Add client" description="Create the customer profile before you start a quote." onClose={closeSheet} notice={message}>
          <form
            id="client-form"
            className="grid gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              void createClient();
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Client name" value={clientName} onChange={(event) => setClientName(event.target.value)} />
              <Field label="Email" type="email" value={clientEmail} onChange={(event) => setClientEmail(event.target.value)} />
              <Field label="Phone" value={clientPhone} onChange={(event) => setClientPhone(event.target.value)} />
              <Field label="Notes" value={clientNotes} onChange={(event) => setClientNotes(event.target.value)} />
            </div>
          </form>
          <SheetFooter>
            <Button variant="secondary" type="button" onClick={closeSheet}>
              Cancel
            </Button>
            <Button
              icon={<UserPlus className="size-4" aria-hidden="true" />}
              type="submit"
              form="client-form"
              disabled={busy || !clientName.trim()}
            >
              Save client
            </Button>
          </SheetFooter>
        </BottomSheet>
      ) : null}

      {activeSheet === "vehicle" ? (
        <BottomSheet title={editingVehicleId ? "Edit vehicle" : "Add vehicle"} description="Tie the auto detail target to an existing client." onClose={closeSheet} notice={message}>
          <form
            id="vehicle-form"
            className="grid gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              void createVehicle();
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <SelectField label="Client" value={vehicleClientId} onChange={(event) => setVehicleClientId(event.target.value)}>
                <option value="">Choose client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </SelectField>
              <Field label="Vehicle label" value={vehicleLabel} onChange={(event) => setVehicleLabel(event.target.value)} />
              <div className="grid gap-2 sm:col-span-2">
                {savedAddresses.length > 0 ? (
                  <div className="grid gap-2">
                    <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-muted">Saved addresses</p>
                    <div className="flex flex-wrap gap-2">
                      {savedAddresses.map((address) => (
                        <button
                          key={address}
                          type="button"
                          className={`min-h-10 rounded-token border px-3 py-2 text-left text-sm font-bold transition ${
                            vehicleAddress.trim() === address
                              ? "border-primary bg-surface-soft text-primary"
                              : "border-border/50 bg-surface text-text hover:bg-surface-soft"
                          }`}
                          onClick={() => setVehicleAddress(address)}
                        >
                          {address}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
                <Field label="Address" value={vehicleAddress} onChange={(event) => setVehicleAddress(event.target.value)} />
              </div>
              <Field label="Year" value={vehicleYear} onChange={(event) => setVehicleYear(event.target.value)} />
              <Field label="Make" value={vehicleMake} onChange={(event) => setVehicleMake(event.target.value)} />
              <Field label="Model" value={vehicleModel} onChange={(event) => setVehicleModel(event.target.value)} />
            </div>
          </form>
          <SheetFooter>
            <Button variant="secondary" type="button" onClick={closeSheet}>
              Cancel
            </Button>
            <Button
              icon={<Car className="size-4" aria-hidden="true" />}
              type="submit"
              form="vehicle-form"
              disabled={busy || !vehicleClientId || !vehicleLabel.trim()}
            >
              {editingVehicleId ? "Update vehicle" : "Save vehicle"}
            </Button>
          </SheetFooter>
        </BottomSheet>
      ) : null}

      {activeSheet === "remove-vehicle" && targetPendingRemoval ? (
        <BottomSheet title="Remove vehicle" description="This will hide the vehicle from the client record." onClose={closeSheet} notice={message}>
          <div className="grid gap-3">
            <div className="rounded-token border border-border/45 bg-surface-soft px-4 py-3">
              <p className="font-extrabold text-text">{targetPendingRemoval.label}</p>
              <p className="mt-1 text-sm text-muted">{describeTarget(targetPendingRemoval)}</p>
            </div>
            <p className="text-sm text-muted">You can add it again later, but it will stop showing in the active client list right away.</p>
          </div>
          <SheetFooter>
            <Button variant="secondary" type="button" onClick={closeSheet}>
              Cancel
            </Button>
            <Button
              icon={<Trash2 className="size-4" aria-hidden="true" />}
              type="button"
              disabled={busy}
              onClick={() => {
                void removeVehicle();
              }}
            >
              Remove vehicle
            </Button>
          </SheetFooter>
        </BottomSheet>
      ) : null}
    </div>
  );
}

function SectionHeader({
  icon: Icon,
  title,
  description
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  description?: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <IconMedallion icon={Icon} />
      <div>
        <h1 className="text-[2.15rem] font-black tracking-[-0.04em] text-text sm:text-[2.35rem]">{title}</h1>
        {description ? <p className="mt-1 max-w-2xl text-sm leading-6 text-muted">{description}</p> : null}
      </div>
    </div>
  );
}

function BottomSheet({
  title,
  description,
  notice,
  onClose,
  children
}: {
  title: string;
  description: string;
  notice?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-slate-950/40 px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur-sm sm:items-center sm:pb-3">
      <button aria-label="Close sheet" className="absolute inset-0 cursor-default" type="button" onClick={onClose} />
      <div
        className="relative z-10 flex w-full max-w-2xl flex-col overflow-hidden rounded-t-[2rem] border border-border/60 bg-surface shadow-float sm:max-h-[88dvh] sm:rounded-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${title.toLowerCase().replaceAll(" ", "-")}-title`}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border/50 bg-gradient-to-b from-surface to-surface-soft px-4 pb-4 pt-4 sm:px-5">
          <div className="flex items-start gap-3">
            <IconMedallion icon={title === "Add client" ? UserPlus : Car} />
            <div>
              <h2 id={`${title.toLowerCase().replaceAll(" ", "-")}-title`} className="text-lg font-extrabold">
                {title}
              </h2>
              <p className="mt-1 text-sm text-muted">{description}</p>
            </div>
          </div>
          <button
            className="inline-grid size-10 shrink-0 place-items-center rounded-full border border-border/70 bg-elevated text-text transition hover:bg-surface-soft"
            type="button"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>

        <div className="grid gap-4 overflow-y-auto px-4 py-4 sm:px-5">
          {notice ? <p className="rounded-token border border-border/60 bg-surface-soft px-3 py-2 text-sm font-medium text-muted">{notice}</p> : null}
          {children}
        </div>
      </div>
    </div>
  );
}

function SheetFooter({ children }: { children: ReactNode }) {
  return (
    <div className="border-t border-border/50 bg-surface px-4 py-4 sm:px-5">
      <div className="grid gap-2 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function VehicleRow({ target, onEdit, onRemove }: { target: ServiceTargetDTO; onEdit: () => void; onRemove: () => void }) {
  return (
    <div className="flex items-start gap-3 rounded-[1rem] border border-border/35 bg-surface/70 p-3">
      <IconMedallion icon={Car} />
      <div className="min-w-0 flex-1">
        <p className="font-extrabold">{target.label}</p>
        <p className="text-sm font-medium text-muted">{describeTarget(target)}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            variant="ghost"
            icon={<Pencil className="size-4" aria-hidden="true" />}
            type="button"
            className="min-h-9 rounded-token border border-border/45 bg-surface px-2.5 py-1.5 text-xs text-text"
            onClick={onEdit}
          >
            Edit
          </Button>
          <Button
            variant="ghost"
            icon={<Trash2 className="size-4" aria-hidden="true" />}
            type="button"
            className="min-h-9 rounded-token border border-border/45 bg-surface px-2.5 py-1.5 text-xs text-text"
            onClick={onRemove}
          >
            Remove
          </Button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onAddClient }: { onAddClient: () => void }) {
  return (
    <div className="grid min-h-[30rem] place-items-center px-4 py-14 text-center">
      <div className="mx-auto grid max-w-sm justify-items-center gap-6 text-center">
        <span className="inline-grid size-24 place-items-center rounded-[12px] border border-border/35 bg-surface-soft">
          <Sparkles className="size-10 text-primary" aria-hidden="true" />
        </span>
        <div className="grid gap-2">
          <h2 className="text-[1.75rem] font-extrabold tracking-[-0.03em] text-text sm:text-[1.9rem]">No clients registered yet</h2>
          <p className="mx-auto max-w-[18rem] text-[0.95rem] font-medium leading-6 text-muted">Start with the person who pays you, then attach the vehicle when the job becomes real.</p>
        </div>
        <Button
          icon={<UserPlus className="size-4" aria-hidden="true" />}
          type="button"
          onClick={onAddClient}
          className="min-h-14 min-w-[16rem] rounded-[12px] border-transparent bg-text px-6 py-3 text-[0.95rem] font-extrabold text-primary-contrast shadow-none hover:bg-text/92"
        >
          Add first client
        </Button>
      </div>
    </div>
  );
}

function SelectField({ label, children, ...props }: SelectHTMLAttributes<HTMLSelectElement> & { label: string; children: ReactNode }) {
  return (
    <label className="grid gap-2 text-sm font-medium text-text">
      <span>{label}</span>
      <select className="min-h-12 rounded-token border border-border/70 bg-elevated px-3 py-2 text-base text-text" {...props}>
        {children}
      </select>
    </label>
  );
}

function IconMedallion({ icon: Icon }: { icon: ComponentType<{ className?: string }> }) {
  return (
    <span className="inline-grid size-10 shrink-0 place-items-center rounded-full border border-border/50 bg-surface-soft text-primary">
      <Icon className="size-5" />
    </span>
  );
}

function describeTarget(target: ServiceTargetDTO) {
  const details = target.details as Record<string, unknown> | null;
  const parts = [details?.year, details?.make, details?.model].filter(Boolean).map((item) => String(item));
  return parts.length > 0 ? `${parts.join(" ")}${target.address ? ` · ${target.address}` : ""}` : target.address ?? "Vehicle profile";
}

function stringValue(value: unknown) {
  return typeof value === "string" ? value : "";
}
